import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Building2, Users, X, Heart, Bot } from "lucide-react";

export const WhoSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="who"
      data-testid="who-section"
      ref={ref}
      className="min-h-screen bg-[#7A9E7E]/10 py-24 md:py-32"
    >
      <div className="max-w-screen-xl mx-auto px-6 md:px-12">
        
        {/* Opening */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="max-w-3xl mb-16 md:mb-20"
        >
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-[#7A9E7E] mb-6 block">
            Who We Are
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#2C2C2C] leading-[1.1]">
            Just two guys who
            <br />
            <span className="italic">love meeting people.</span>
          </h2>
        </motion.div>

        {/* Not a Company - Visual Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-wrap items-center gap-4 mb-16"
        >
          <div className="flex items-center gap-3 bg-[#C17A54] text-white px-5 py-3 rounded-full">
            <div className="relative">
              <Building2 className="w-5 h-5" />
              <X className="w-3 h-3 absolute -top-1 -right-1" strokeWidth={3} />
            </div>
            <span className="font-sans text-sm">Not a company</span>
          </div>
          <div className="flex items-center gap-3 bg-[#2C2C2C] text-white px-5 py-3 rounded-full">
            <div className="relative">
              <Bot className="w-5 h-5" />
              <X className="w-3 h-3 absolute -top-1 -right-1" strokeWidth={3} />
            </div>
            <span className="font-sans text-sm">No AI, ever</span>
          </div>
          <div className="flex items-center gap-3 bg-[#C17A54] text-white px-5 py-3 rounded-full">
            <div className="relative">
              <Users className="w-5 h-5" />
              <X className="w-3 h-3 absolute -top-1 -right-1" strokeWidth={3} />
            </div>
            <span className="font-sans text-sm">No networking BS</span>
          </div>
          <div className="flex items-center gap-3 bg-[#7A9E7E] text-white px-5 py-3 rounded-full">
            <Heart className="w-5 h-5" fill="currentColor" />
            <span className="font-sans text-sm">Just genuine curiosity</span>
          </div>
        </motion.div>

        {/* The Two Humans */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-20">
          
          {/* Image/Visual - Creative Treatment */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            {/* Main polaroid-style frame */}
            <div className="relative bg-white p-3 pb-16 rounded-sm shadow-xl rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
              <div className="relative aspect-square overflow-hidden">
                <img
                  src="/founders.png"
                  alt="Azhar and Faiyaz"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Handwritten-style names */}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-6">
                <span className="font-serif italic text-xl text-[#2C2C2C]">Azhar</span>
                <span className="text-[#E8A838]">&</span>
                <span className="font-serif italic text-xl text-[#2C2C2C]">Faiyaz</span>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#7A9E7E] rounded-full opacity-20 blur-xl" />
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#E8A838] rounded-full opacity-20 blur-xl" />
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col justify-center"
          >
            <p className="font-sans text-lg md:text-xl text-[#2C2C2C]/70 leading-relaxed mb-6">
              We're the friends who'll talk to anyone—the street vendor, the CEO, 
              the artist, the cab driver. Not to network. 
              <span className="text-[#2C2C2C] font-medium"> Just because people are fascinating.</span>
            </p>
            <p className="font-sans text-lg md:text-xl text-[#2C2C2C]/70 leading-relaxed mb-8">
              Over the years, we've sat with hundreds of strangers. Each one 
              taught us something. Each one had a story worth hearing.
            </p>
            
            {/* Quote */}
            <div className="border-l-2 border-[#E8A838] pl-5">
              <p className="font-serif italic text-xl text-[#2C2C2C]/60">
                "Our friends said we should start a podcast. We thought—why not 
                just be there for people instead?"
              </p>
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};
